import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "../styles/PfpGenerator.css";
import {
  getImagePaths,
  getOptimizedImagePath,
  getAllTraits,
} from "../utils/imageLoader";
import Toast from "./Toast";
import html2canvas from "html2canvas";

// 定义层级顺序常量
const LAYER_ORDER = [
  "BACKGROUND",
  "BASE",
  "EYEBROW",
  "EYE",
  "HAIR",
  "OUTFIT",
  "FACE ACC",
  "MOUTH",
  "HAND",
  "HAND BASE",
  "HAIR ACC",
  "BACKGROUND ACC",
  "MONAD LOGO",
];

const REQUIRED_TRAITS = [
  "MOUTH",
  "OUTFIT",
  "EYEBROW",
  "HAIR",
  "BACKGROUND",
  "HAND",
];

const PfpGenerator = () => {
  const [selectedGender, setSelectedGender] = useState("male");
  const [maleTraits, setMaleTraits] = useState({});
  const [femaleTraits, setFemaleTraits] = useState({});
  const [activeCategory, setActiveCategory] = useState("BACKGROUND");
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadedImagePaths, setLoadedImagePaths] = useState({}); // 存储优化后的图片路径
  const [toasts, setToasts] = useState([]);

  // 获取所有特征数据 - 使用优化版本
  const { maleTraits: allMaleTraits, femaleTraits: allFemaleTraits } =
    getAllTraits();
  const currentTraits = selectedGender === "male" ? maleTraits : femaleTraits;
  const allCurrentTraits =
    selectedGender === "male" ? allMaleTraits : allFemaleTraits;

  // 获取类别列表
  const categories = Object.keys(allCurrentTraits);

  // 自动随机生成初始特征
  useEffect(() => {
    // 只在组件首次加载时生成随机特征
    if (Object.keys(maleTraits).length === 0) {
      generateRandomTraits("male");
    }
  }, []); // 移除 generateRandomTraits 依赖

  // 预加载图片并获取最佳路径
  const loadImage = async (originalPath) => {
    if (!originalPath) return;

    // 如果图片已经加载过，直接返回
    if (loadedImagePaths[originalPath]) {
      return loadedImagePaths[originalPath];
    }

    try {
      const optimizedPath = getOptimizedImagePath(originalPath);
      setLoadedImagePaths((prev) => ({
        ...prev,
        [originalPath]: optimizedPath,
      }));
      return optimizedPath;
    } catch (error) {
      console.error("Error loading image:", error);
      return originalPath;
    }
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
    const genderTraits = gender === "male" ? maleTraits : femaleTraits;

    // 如果该性别没有选择的特征或特征为空，自动生成随机特征
    if (Object.keys(genderTraits).length === 0) {
      generateRandomTraits(gender);
    }
  };

  // 添加提示消息的函数
  const showMessage = (message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message }]);
  };

  // 移除提示消息的函数
  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleTraitChange = (category, imagePath) => {
    // 不允许必需特征为空，但是允许 HAND 为空
    if (REQUIRED_TRAITS.includes(category) && !imagePath && category !== "HAND") {
      showMessage(`${category} is a required trait and cannot be empty.`);
      return;
    }

    if (selectedGender === "male") {
      setMaleTraits((prev) => {
        // 如果是选择 HAND BASE，先检查当前的 HAND 是否是 hide-hand
        if (category === "HAND BASE") {
          const currentHand = prev["HAND"];
          const isHideHand =
            currentHand &&
            currentHand.split("/").pop().toLowerCase().startsWith("hide-hand");
          if (isHideHand || !currentHand) {  // 添加 !currentHand 条件
            showMessage("You cannot select HAND BASE when HAND is hidden or not selected.");
            return prev; // 如果是 hide-hand 或 None，不允许选择 HAND BASE
          }
        }

        const newTraits = {
          ...prev,
          [category]: imagePath,
        };

        // 处理 HAND 和 HAND BASE 的联动
        if (category === "HAND") {
          const fileName = imagePath
            ? imagePath.split("/").pop().toLowerCase()
            : "";
          // 如果选择了 hide-hand 前缀的图片或者是 None，清除 HAND BASE
          if (!imagePath || fileName.startsWith("hide-hand")) {
            newTraits["HAND BASE"] = null;
            if (fileName.startsWith("hide-hand")) {
              showMessage("HAND BASE will be hidden when using a hidden hand style.");
            } else if (!imagePath) {
              showMessage("HAND BASE will be hidden when HAND is set to None.");
            }
          } else {
            // 如果之前是 hide-hand 或 None，现在换成其他手，需要恢复 HAND BASE
            const prevFileName = prev["HAND"]
              ? prev["HAND"].split("/").pop().toLowerCase()
              : "";
            if ((!prev["HAND"] || prevFileName.startsWith("hide-hand")) && prev["BASE"]) {
              // 根据当前的 BASE 选择对应的 HAND BASE
              const baseFileName = prev["BASE"].split("/").pop();
              const baseNumber = baseFileName.split(" ")[0];
              const handBaseOptions = allMaleTraits["HAND BASE"] || [];

              const matchingHandBase = handBaseOptions.find((handBasePath) => {
                const handBaseFileName = handBasePath.split("/").pop();
                const handBaseNumber = handBaseFileName.split(" ")[0];
                return handBaseNumber === baseNumber;
              });

              if (matchingHandBase) {
                newTraits["HAND BASE"] = matchingHandBase;
              }
            }
          }
        }

        // 联动逻辑：当选择BASE时，自动更新HAND BASE
        if (category === "BASE" && imagePath) {
          const baseFileName = imagePath.split("/").pop();
          const baseNumber = baseFileName.split(" ")[0];
          const handBaseOptions = allMaleTraits["HAND BASE"] || [];

          // 只有当当前的 HAND 存在且不是 hide-hand 时才设置 HAND BASE
          const currentHand = newTraits["HAND"];
          if (currentHand && !currentHand.split("/").pop().toLowerCase().startsWith("hide-hand")) {
            const matchingHandBase = handBaseOptions.find((handBasePath) => {
              const handBaseFileName = handBasePath.split("/").pop();
              const handBaseNumber = handBaseFileName.split(" ")[0];
              return handBaseNumber === baseNumber;
            });

            if (matchingHandBase) {
              newTraits["HAND BASE"] = matchingHandBase;
              showMessage("HAND BASE has been automatically updated to match the skin tone.");
            }
          }
        }

        // 新增联动逻辑：当选择HAND BASE时，自动更新BASE
        if (category === "HAND BASE" && imagePath) {
          const handBaseFileName = imagePath.split("/").pop();
          const handBaseNumber = handBaseFileName.split(" ")[0];
          const baseOptions = allMaleTraits["BASE"] || [];

          const matchingBase = baseOptions.find((basePath) => {
            const baseFileName = basePath.split("/").pop();
            const baseNumber = baseFileName.split(" ")[0];
            return baseNumber === handBaseNumber;
          });

          if (matchingBase) {
            newTraits["BASE"] = matchingBase;
          }
        }

        return newTraits;
      });
    } else {
      setFemaleTraits((prev) => {
        // 如果是选择 HAND BASE，先检查当前的 HAND 是否是 hide-hand
        if (category === "HAND BASE") {
          const currentHand = prev["HAND"];
          const isHideHand =
            currentHand &&
            currentHand.split("/").pop().toLowerCase().startsWith("hide-hand");
          if (isHideHand || !currentHand) {  // 添加 !currentHand 条件
            showMessage("You cannot select HAND BASE when HAND is hidden or not selected.");
            return prev; // 如果是 hide-hand 或 None，不允许选择 HAND BASE
          }
        }

        const newTraits = {
          ...prev,
          [category]: imagePath,
        };

        // 处理 HAND 和 HAND BASE 的联动
        if (category === "HAND") {
          const fileName = imagePath
            ? imagePath.split("/").pop().toLowerCase()
            : "";
          // 如果选择了 hide-hand 前缀的图片或者是 None，清除 HAND BASE
          if (!imagePath || fileName.startsWith("hide-hand")) {
            newTraits["HAND BASE"] = null;
            if (fileName.startsWith("hide-hand")) {
              showMessage("HAND BASE will be hidden when using a hidden hand style.");
            } else if (!imagePath) {
              showMessage("HAND BASE will be hidden when HAND is set to None.");
            }
          } else {
            // 如果之前是 hide-hand 或 None，现在换成其他手，需要恢复 HAND BASE
            const prevFileName = prev["HAND"]
              ? prev["HAND"].split("/").pop().toLowerCase()
              : "";
            if ((!prev["HAND"] || prevFileName.startsWith("hide-hand")) && prev["BASE"]) {
              // 根据当前的 BASE 选择对应的 HAND BASE
              const baseFileName = prev["BASE"].split("/").pop();
              const baseNumber = baseFileName.split(" ")[0];
              const handBaseOptions = allFemaleTraits["HAND BASE"] || [];

              const matchingHandBase = handBaseOptions.find((handBasePath) => {
                const handBaseFileName = handBasePath.split("/").pop();
                const handBaseNumber = handBaseFileName.split(" ")[0];
                return handBaseNumber === baseNumber;
              });

              if (matchingHandBase) {
                newTraits["HAND BASE"] = matchingHandBase;
              }
            }
          }
        }

        // 对女性角色应用相同的联动逻辑
        if (category === "BASE" && imagePath) {
          const baseFileName = imagePath.split("/").pop();
          const baseNumber = baseFileName.split(" ")[0];
          const handBaseOptions = allFemaleTraits["HAND BASE"] || [];

          // 只有当当前的 HAND 存在且不是 hide-hand 时才设置 HAND BASE
          const currentHand = newTraits["HAND"];
          if (currentHand && !currentHand.split("/").pop().toLowerCase().startsWith("hide-hand")) {
            const matchingHandBase = handBaseOptions.find((handBasePath) => {
              const handBaseFileName = handBasePath.split("/").pop();
              const handBaseNumber = handBaseFileName.split(" ")[0];
              return handBaseNumber === baseNumber;
            });

            if (matchingHandBase) {
              newTraits["HAND BASE"] = matchingHandBase;
              showMessage("HAND BASE has been automatically updated to match the skin tone.");
            }
          }
        }

        // 新增联动逻辑：当选择HAND BASE时，自动更新BASE
        if (category === "HAND BASE" && imagePath) {
          const handBaseFileName = imagePath.split("/").pop();
          const handBaseNumber = handBaseFileName.split(" ")[0];
          const baseOptions = allFemaleTraits["BASE"] || [];

          const matchingBase = baseOptions.find((basePath) => {
            const baseFileName = basePath.split("/").pop();
            const baseNumber = baseFileName.split(" ")[0];
            return baseNumber === handBaseNumber;
          });

          if (matchingBase) {
            newTraits["BASE"] = matchingBase;
          }
        }

        return newTraits;
      });
    }
  };

  // 将 generateRandomTraits 用 useCallback 包裹
  const generateRandomTraits = React.useCallback(
    (gender) => {
      setIsGenerating(true);
      const traits = {};
      const currentTraits = gender === "male" ? allMaleTraits : allFemaleTraits;

      // 按照层级顺序生成随机特征
      LAYER_ORDER.forEach((category) => {
        if (currentTraits[category]) {
          const options = currentTraits[category];
          if (options.length > 0) {
            // 如果是 HAND，需要检查是否选择了 hide-hand
            if (category === "HAND") {
              // 30% 的概率选择 None
              if (Math.random() < 0.3) {
                traits[category] = null;
                traits["HAND BASE"] = null; // 当 HAND 为 None 时，清除 HAND BASE
              } else {
                const randomIndex = Math.floor(Math.random() * options.length);
                traits[category] = options[randomIndex];

                // 检查是否是 hide-hand
                const fileName = traits[category].split("/").pop().toLowerCase();
                if (fileName.startsWith("hide-hand")) {
                  // 如果是 hide-hand，清除 HAND BASE
                  traits["HAND BASE"] = null;
                }
              }
            }
            // 如果是 HAND BASE，需要检查 HAND 是否存在且不是 hide-hand
            else if (category === "HAND BASE") {
              const currentHand = traits["HAND"];
              // 只有当 HAND 存在且不是 hide-hand 时才设置 HAND BASE
              if (currentHand && !currentHand.split("/").pop().toLowerCase().startsWith("hide-hand")) {
                // 尝试匹配 BASE 的肤色
                if (traits["BASE"]) {
                  const baseFileName = traits["BASE"].split("/").pop();
                  const baseNumber = baseFileName.split(" ")[0];

                  const matchingHandBase = options.find((handBasePath) => {
                    const handBaseFileName = handBasePath.split("/").pop();
                    const handBaseNumber = handBaseFileName.split(" ")[0];
                    return handBaseNumber === baseNumber;
                  });

                  if (matchingHandBase) {
                    traits[category] = matchingHandBase;
                  } else {
                    const randomIndex = Math.floor(Math.random() * options.length);
                    traits[category] = options[randomIndex];
                  }
                } else {
                  const randomIndex = Math.floor(Math.random() * options.length);
                  traits[category] = options[randomIndex];
                }
              } else {
                traits[category] = null;
              }
            }
            // 如果是 BASE，需要根据已选择的 HAND BASE 来选择
            else if (category === "BASE") {
              // 先随机选择一个 BASE
              const randomIndex = Math.floor(Math.random() * options.length);
              traits[category] = options[randomIndex];

              // 如果已经有 HAND BASE，尝试匹配肤色
              if (traits["HAND BASE"]) {
                const handBaseFileName = traits["HAND BASE"].split("/").pop();
                const handBaseNumber = handBaseFileName.split(" ")[0];

                const matchingBase = options.find((basePath) => {
                  const baseFileName = basePath.split("/").pop();
                  const baseNumber = baseFileName.split(" ")[0];
                  return baseNumber === handBaseNumber;
                });

                if (matchingBase) {
                  traits[category] = matchingBase;
                }
              }
            } else {
              // 对于非必需特征，有 15% 的概率选择 None
              if (!REQUIRED_TRAITS.includes(category) && Math.random() < 0.15) {
                traits[category] = null;
              } else {
                const randomIndex = Math.floor(Math.random() * options.length);
                traits[category] = options[randomIndex];
              }
            }
          }
        }
      });

      if (gender === "male") {
        setMaleTraits(traits);
      } else {
        setFemaleTraits(traits);
      }
      setIsGenerating(false);
    },
    [allMaleTraits, allFemaleTraits]
  );

  const clearAllTraits = () => {
    if (selectedGender === "male") {
      setMaleTraits({});
    } else {
      setFemaleTraits({});
    }
  };

  const downloadImage = async () => {
    // 创建高分辨率画布 (3x 分辨率以获得最佳清晰度)
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const scale = 3; // 3倍分辨率，平衡质量和文件大小
    canvas.width = 500 * scale;
    canvas.height = 500 * scale;
    
    // 设置画布缩放以匹配高分辨率
    ctx.scale(scale, scale);
    
    // 启用图像平滑以获得更好的质量
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 预加载所有图片，优先使用原始未压缩图片
    const loadedImages = await Promise.all(
      LAYER_ORDER.map(async (layer) => {
        if (currentTraits[layer]) {
          try {
            // 尝试使用原始路径，避免压缩版本
            const originalPath = currentTraits[layer];
            return new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = "anonymous";
              img.onload = () => resolve({ layer, img, originalPath });
              img.onerror = () => {
                console.error(`Failed to load image: ${originalPath}`);
                resolve({ layer, img: null, originalPath: null });
              };
              // 确保使用原始图片路径，不经过任何压缩处理
              img.src = originalPath;
            });
          } catch (error) {
            console.error(`Error loading image for ${layer}:`, error);
            return { layer, img: null, originalPath: null };
          }
        } else {
          return { layer, img: null, originalPath: null };
        }
      })
    );

    // 按层级顺序绘制图片，处理特殊层级
    loadedImages
      .sort((a, b) => {
        const aIndex = (() => {
          // 如果是 HAIR 且文件名以 cover 开头，则层级高于 OUTFIT
          if (a.layer === "HAIR" && a.originalPath) {
            const fileName = a.originalPath.split("/").pop().toLowerCase();
            if (fileName.startsWith("cover")) {
              return LAYER_ORDER.indexOf("OUTFIT") + 1;
            }
          }
          return LAYER_ORDER.indexOf(a.layer);
        })();
        
        const bIndex = (() => {
          // 如果是 HAIR 且文件名以 cover 开头，则层级高于 OUTFIT
          if (b.layer === "HAIR" && b.originalPath) {
            const fileName = b.originalPath.split("/").pop().toLowerCase();
            if (fileName.startsWith("cover")) {
              return LAYER_ORDER.indexOf("OUTFIT") + 1;
            }
          }
          return LAYER_ORDER.indexOf(b.layer);
        })();
        
        return aIndex - bIndex;
      })
      .forEach(({ img }) => {
        if (img) {
          ctx.drawImage(img, 0, 0, 500, 500);
        }
      });

    // 追踪下载事件和traits使用情况
    if (window.clarity) {
      const traits = selectedGender === "male" ? maleTraits : femaleTraits;
      const traitsUsage = {};
      
      Object.entries(traits).forEach(([category, value]) => {
        if (value) {
          traitsUsage[`${category}`] = value;
        }
      });

      // 发送下载事件
      window.clarity("event", "download_pfp");

      // 使用 Custom Tags 记录详细信息
      window.clarity("set", "pfp_gender", selectedGender);
      
      // 将每个特征作为单独的 tag
      Object.entries(traitsUsage).forEach(([category, value]) => {
        if (value) {
          // 提取文件名作为值
          const fileName = value.split('/').pop().replace(/\.[^/.]+$/, "");
          window.clarity("set", `pfp_trait_${category.toLowerCase()}`, fileName);
        }
      });

      // 记录总特征数
      // window.clarity("set", "pfp_traits_count", Object.keys(traitsUsage).length.toString());
      
      // 记录下载时间
      window.clarity("set", "pfp_download_time", new Date().toISOString());
    }

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pfp-${selectedGender}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="pfp-generator">
      {/* 返回首页按钮 */}
      <Link to="/" className="back-home-btn">
        Back to Home
      </Link>

      <div className="generator-container">
        <div className="left-panel">
          <div className="preview-section">
            <div className="preview-container">
              <div className="preview-canvas">
                {LAYER_ORDER.map(
                  (layer) =>
                    currentTraits[layer] && (
                      <img
                        key={layer}
                        src={getOptimizedImagePath(currentTraits[layer])}
                        alt={layer}
                        className="trait-layer"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit:
                            layer === "BACKGROUND" ? "cover" : "contain",
                          zIndex: (() => {
                            // 如果是 HAIR 且文件名以 cover 开头，则层级高于 OUTFIT
                            if (layer === "HAIR") {
                              const fileName = currentTraits[layer]
                                .split("/")
                                .pop()
                                .toLowerCase();
                              if (fileName.startsWith("cover")) {
                                return LAYER_ORDER.indexOf("OUTFIT") + 1;
                              }
                            }
                            return LAYER_ORDER.indexOf(layer);
                          })(),
                        }}
                        onError={(e) => {
                          console.error(
                            `Failed to load trait image: ${currentTraits[layer]}`
                          );
                          // 尝试加载原始图片作为备用
                          if (e.target.src !== currentTraits[layer]) {
                            e.target.src = currentTraits[layer];
                          } else {
                            e.target.style.display = "none";
                          }
                        }}
                      />
                    )
                )}
              </div>
            </div>
            <div className="action-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => generateRandomTraits(selectedGender)}
                disabled={isGenerating}
              >
                {isGenerating ? "Generating..." : "Random Generate"}
              </button>
              <button
                className="btn btn-success"
                onClick={downloadImage}
                disabled={Object.keys(currentTraits).length === 0}
              >
                Download PFP
              </button>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="gender-selection">
            <h3>Select Gender</h3>
            <div className="gender-buttons">
              <button
                className={`gender-btn ${
                  selectedGender === "male" ? "active" : ""
                }`}
                onClick={() => handleGenderChange("male")}
              >
                Male
              </button>
              <button
                className={`gender-btn ${
                  selectedGender === "female" ? "active" : ""
                }`}
                onClick={() => handleGenderChange("female")}
              >
                Female
              </button>
            </div>
          </div>

          <div className="traits-selection">
            <h3>Customize Traits</h3>

            {/* 类别标签 */}
            <div className="category-tabs">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-tab ${
                    activeCategory === category ? "active" : ""
                  }`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* 当前类别的特征选项 */}
            <div className="traits-container">
              {allCurrentTraits[activeCategory] && (
                <div className="trait-category">
                  <div className="trait-options">
                    {/* 显示 HAND 的 None 选项和其他非必需特征的 None 选项，排除特定类别 */}
                    {(activeCategory === "HAND" || 
                      (!REQUIRED_TRAITS.includes(activeCategory) &&
                        activeCategory !== "BASE" &&
                        activeCategory !== "HAND BASE")) && (
                      <div
                        className={`trait-option ${
                          !currentTraits[activeCategory] ? "selected" : ""
                        }`}
                        onClick={() =>
                          handleTraitChange(activeCategory, null)
                        }
                      >
                        <div className="trait-preview none-option">
                          <span>None</span>
                        </div>
                      </div>
                    )}
                    {allCurrentTraits[activeCategory].map(
                      (imagePath, index) => (
                        <div
                          key={index}
                          className={`trait-option ${
                            currentTraits[activeCategory] === imagePath
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            handleTraitChange(activeCategory, imagePath)
                          }
                        >
                          <div className="trait-preview">
                            <img
                              src={imagePath}
                              alt={`${activeCategory} ${index}`}
                              onError={(e) => {
                                console.error(
                                  `Failed to load preview: ${imagePath}`
                                );
                                // 尝试加载原始路径作为备用
                                const originalPath = imagePath
                                  .replace(/^\/webp\//, "/")
                                  .replace(/^\/optimized\//, "/");
                                if (e.target.src !== originalPath) {
                                  e.target.src = originalPath;
                                } else {
                                  e.target.style.display = "none";
                                  e.target.parentNode.innerHTML = `<span style="font-size: 10px;">Error</span>`;
                                }
                              }}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast 消息 */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default PfpGenerator;
